import { Outlet } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Typography } from "@mui/material";
import { MdLockOutline } from "react-icons/md";
import { PermissionsModel } from "../../Redux/Slices/InitialStatePropsModel";

interface PermissionGuardProps {
  permissionKey: string;
}

/**
 * Where to send an employee after login.
 *
 * This used to ignore its argument and always return "/dashboard" on the
 * assumption that the dashboard was open to every logged-in employee. Once the
 * dashboard endpoints were permission-guarded that stopped being true, and an
 * employee without the module landed on a page whose every widget 403'd.
 *
 * Order is deliberate: the dashboard is the natural home for anyone who can see
 * it, but it is commercial data, so products is the fallback -- the module most
 * employees hold. Anything they can view beats a page they cannot.
 */
const LANDING_PRIORITY = [
  { module: "dashboard", route: "/dashboard" },
  { module: "products", route: "/products" },
  { module: "orders", route: "/orders" },
  { module: "categories", route: "/categories" },
  { module: "customers", route: "/customers" },
  { module: "inventories", route: "/inventory-validate" },
  { module: "supplier", route: "/supplier" },
  { module: "reports", route: "/product-reports" },
  { module: "employee", route: "/employee" },
  { module: "roles", route: "/roles" },
  { module: "customer-help-service", route: "/customer-help-service" },
  { module: "home-screen", route: "/home-screen" },
  { module: "cms", route: "/cms" },
  { module: "profile", route: "/taxation" },
  { module: "settings", route: "/adminSettings" },
];

const canView = (permissions: any, module: string): boolean =>
  permissions?.[module]?.view === true || permissions?.[module]?.edit === true;

export const getFirstPermittedRoute = (permissions: any): string => {
  const hit = LANDING_PRIORITY.find((entry) => canView(permissions, entry.module));
  // No module at all: land on the dashboard and let the guard below explain.
  // Better a clear message than a redirect loop between forbidden routes.
  return hit ? hit.route : "/dashboard";
};

export const NotAuthorized = () => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      minHeight: "60vh",
      gap: 1.5,
      px: 3,
    }}
  >
    <MdLockOutline size={44} style={{ opacity: 0.45 }} />
    <Typography variant="h6" sx={{ fontWeight: 600 }}>
      You are not authorized to view this page
    </Typography>
    <Typography variant="body2" sx={{ color: "text.secondary", maxWidth: 420 }}>
      Your role does not include access to this section. Ask an administrator if
      you need it.
    </Typography>
  </Box>
);

const PermissionGuard = ({ permissionKey }: PermissionGuardProps) => {
  const permissions: PermissionsModel = useSelector(
    (state: any) => state.AuthSlice.adminAuthUser?.permissions,
  );

  // Show the message rather than redirecting. Redirecting hid the reason and,
  // once every landing candidate could itself be forbidden, risked bouncing
  // between routes.
  return canView(permissions, permissionKey) ? <Outlet /> : <NotAuthorized />;
};

export default PermissionGuard;
